import React, { useEffect, useRef } from 'react';
import spendChart from '../../../assets/landing/spend-chart.png';
import SectionShape from '../../../components/SectionShape';

/**
 * Spending section (#spending) — the interpreted "Spending Insight" card.
 * This is the interpretation-led surface (the future "Daily Brief" hero):
 * it leads with what the number MEANS, not the raw number.
 *
 * The card's figures count up from zero when scrolled into view.
 * Self-contained IntersectionObserver + rAF counter — no GSAP, no deps.
 *
 * The background growth curve is a direct child of <section> (which spans
 * the full browser width) so it reaches both edges without needing a
 * viewport breakout hack. All real content sits in zIndex:1 wrappers above it.
 */

const mono = "'JetBrains Mono', ui-monospace, monospace";

// Animated number that counts up when it scrolls into view.
function Count({ staticValue = false, value, prefix = '', suffix = '', decimals = 0, color }) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const format = (n) =>
      prefix +
      n.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }) +
      suffix;

    // final value shown immediately if reduced motion
    if (prefersReduced || staticValue) {
      el.textContent = format(value);
      return;
    }

    el.textContent = format(0);
    let raf = 0;
    let started = false;

    const run = () => {
      const duration = 1100;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min((now - start) / duration, 1);
        // easeOutCubic
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = format(value * eased);
        if (t < 1) raf = requestAnimationFrame(tick);
        else el.textContent = format(value);
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            started = true;
            run();
            io.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [value, prefix, suffix, decimals, staticValue]);

  return (
    <strong ref={ref} style={{ fontWeight: 800, color: color || 'inherit' }}>
      {prefix}
      {value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </strong>
  );
}

export default function Spending({ mobile = false }) {
  return (
    <section id="spending" style={{ position: 'relative' }}>
      {/* Background growth curve — direct child of <section>, spans full browser width */}
      {!mobile && <SectionShape kind="growth" opacity={0.5} />}

      <div className="mobile-section-inner"
        style={{
          position: 'relative',
          zIndex: 1,
          padding: 'clamp(56px,7vw,88px) clamp(20px,5vw,56px)',
          maxWidth: 'var(--max-width)',
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <div data-rv="up" style={{ marginBottom: 'clamp(40px,5vw,64px)' }}>
          <div
            style={{
              fontFamily: mono,
              color: 'var(--kachng-green)',
              fontSize: '12px',
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              fontWeight: 500,
              marginBottom: '24px',
            }}
          >
            Spending intelligence
          </div>
          <h2
            style={{
              margin: '0 0 20px',
              fontSize: 'clamp(34px,4.4vw,56px)',
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.02,
              textWrap: 'balance',
            }}
          >
            Not just what you spent. What it means.
          </h2>
          <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.6, color: 'var(--text-300)' }}>
            {mobile ? "See what you bought and how it compares with your normal spending." : "KACHNG processes every line item — not just the total. See what you bought, where you bought it, and how it compares with your normal spending."}
          </p>
        </div>

        <div className="spending-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))',
            gap: 'clamp(32px,4vw,56px)',
            alignItems: 'center',
          }}
        >
          <div data-rv="left">
            <div className="spending-insight"
              style={{
                background:
                  'radial-gradient(120% 90% at 50% 0%, var(--card-grad-top) 0%, var(--card-grad-bottom) 65%)',
                border: '1px solid var(--border-soft)',
                boxShadow: '0 20px 56px rgba(0,0,0,0.5)',
                borderRadius: '20px',
                padding: '28px 32px',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    background: 'var(--green-accent-bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--kachng-green)',
                    fontSize: '17px',
                    flex: 'none',
                  }}
                >
                  ◉
                </div>
                <div style={{ flex: 'none' }}>
                  <div
                    style={{
                      fontFamily: mono,
                      color: 'var(--kachng-green)',
                      fontSize: '11px',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Only on KACHNG
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Spending Insight</div>
                </div>
              </div>

              <p
                style={{
                  margin: 0,
                  fontSize: 'clamp(18px,1.8vw,21px)',
                  lineHeight: 1.55,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {mobile ? <><Count staticValue value={741.63} prefix="$" decimals={2} /> at Whole Foods Market this month — <Count staticValue value={42} suffix="%" color="var(--kachng-green)" /> of your entire month’s spending.</> : <><Count staticValue={mobile} value={164.19} prefix="$" decimals={2} /> at Whole Foods Market is{' '}
                <Count staticValue={mobile} value={21} suffix="x" /> your usual grocery run — and you've still been back
                twice more this month for a total of{' '}
                <Count staticValue={mobile} value={741.63} prefix="$" decimals={2} />. Whole Foods quietly ate{' '}
                <Count staticValue={mobile} value={42} suffix="%" color="var(--kachng-green)" /> of your entire month.</>}
              </p>
            </div>

            <p className="spending-secondary"
              style={{
                margin: '24px 4px 0',
                fontSize: '15px',
                lineHeight: 1.6,
                color: 'var(--text-muted)',
                maxWidth: '56ch',
              }}
            >
              No bank connection. No manual entry. Just the receipts merchants already send — which
              is why KACHNG can understand what was actually purchased.
            </p>
          </div>

          {!mobile && <div data-rv="blur" style={{ position: 'relative' }}>
            <img
              src={spendChart}
              alt="Monthly spending tracked — $1,751.66 across 34 receipts"
              style={{
                width: 'min(520px,100%)',
                display: 'block',
                margin: '0 auto',
                border: '1px solid var(--border-input)',
                borderRadius: '24px',
                position: 'relative',
              }}
            />
          </div>}
        </div>
      </div>
    </section>
  );
}